import { useState } from "react";

interface PlayerDemoProps {
  poster: string;
  title: string;
}

type PlayerState = "idle" | "loading" | "ready";

export default function PlayerDemo({ poster, title }: PlayerDemoProps) {
  const [state, setState] = useState<PlayerState>("idle");

  const handlePlay = () => {
    if (state === "loading") return;
    setState("loading");
    window.setTimeout(() => setState("ready"), 1400);
  };

  const reset = () => setState("idle");

  return (
    <div className="rounded-lg overflow-hidden border border-background-300/60 bg-background-100">
      <div className="relative w-full aspect-video bg-background-50">
        <img
          src={poster}
          alt={`${title} 播放界面`}
          title={`${title} 在线观看界面`}
          className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-background-50/30"></div>

        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-6">
          {state === "idle" && (
            <>
              <button
                type="button"
                onClick={handlePlay}
                aria-label="播放（界面演示）"
                className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-play-fill text-3xl md:text-4xl pl-1"></i>
              </button>
              <p className="mt-4 text-sm md:text-base text-foreground-800 font-medium">点击播放 · 高清体验</p>
              <p className="mt-1 text-xs text-foreground-600">当前为播放界面演示，不提供真实视频流</p>
            </>
          )}

          {state === "loading" && (
            <>
              <span className="w-14 h-14 rounded-full border-4 border-background-300/70 border-t-primary-500 animate-spin-slow"></span>
              <p className="mt-4 text-sm text-foreground-700">正在加载播放资源...</p>
            </>
          )}

          {state === "ready" && (
            <>
              <span className="w-14 h-14 rounded-full bg-accent-500/20 border border-accent-400/50 text-accent-300 flex items-center justify-center">
                <i className="ri-information-line text-2xl"></i>
              </span>
              <h3 className="mt-4 font-heading font-bold text-base md:text-lg text-foreground-950">播放界面演示</h3>
              <p className="mt-2 max-w-md text-xs md:text-sm text-foreground-600">
                这里是播放器的界面展示区域。本作品为内容演示，未接入真实视频流，因此不会出现虚假的播放成功状态。
              </p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-background-200/80 border border-background-300/70 text-foreground-950 text-sm font-medium hover:border-primary-500/60 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重新加载
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-background-300/60">
        <div className="flex items-center gap-3 text-xs md:text-sm text-foreground-600">
          <span className="flex items-center gap-1.5">
            <i className="ri-hd-line text-primary-400"></i> 1080P 高清
          </span>
          <span className="hidden sm:flex items-center gap-1.5">
            <i className="ri-volume-up-line"></i> 立体声
          </span>
          <span className="hidden sm:flex items-center gap-1.5">
            <i className="ri-closed-captioning-line"></i> 中文字幕
          </span>
        </div>
        <span className="text-xs text-foreground-500 font-label">DEMO PLAYER</span>
      </div>
    </div>
  );
}